/**
 * GateBars — sorted gate pass rate bars with expandable details,
 * failing creative drilldown to Creatives tab, and CSV export.
 * Stub file — full implementation in Task 2.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { GatesSkeleton } from '@/components/dashboard/shared/SkeletonLoaders';
import { useGateSummary } from '@/hooks/useDashboard';
import { fmtPct } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { GateStats } from '@/types/dashboard';

// ============================================================================
// HELPERS
// ============================================================================

function getGateColor(passRate: number): string {
  if (passRate >= 80) return '#22C55E';
  if (passRate >= 60) return '#F59E0B';
  return '#EF4444';
}

// ============================================================================
// GATE ROW
// ============================================================================

interface GateRowProps {
  gate: GateStats;
  isExpanded: boolean;
  onToggle: () => void;
  isOdd: boolean;
  onDrilldown: (gateId: string) => void;
}

function GateRow({ gate, isExpanded, onToggle, isOdd, onDrilldown }: GateRowProps) {
  const color = getGateColor(gate.passRate);
  const navigate = useNavigate();

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors rounded-lg',
          isOdd ? 'bg-surface-subtle' : 'bg-surface',
          'hover:bg-surface-subtle'
        )}
        onClick={onToggle}
      >
        {/* Gate name + role badge */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-primary truncate">{gate.name}</span>
            {gate.role === 'critical' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-danger/10 text-danger font-medium flex-shrink-0">
                critical
              </span>
            )}
          </div>
        </div>

        {/* Pass rate bar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-24 h-2 bg-border-subtle rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${gate.passRate}%`, backgroundColor: color }}
            />
          </div>
          <span className="text-sm font-semibold w-10 text-right" style={{ color }}>
            {fmtPct(gate.passRate)}
          </span>
        </div>

        {/* Trend */}
        <span
          className={cn(
            'text-xs w-20 text-right flex-shrink-0',
            gate.trend > 0 ? 'text-success' : gate.trend < 0 ? 'text-danger' : 'text-secondary'
          )}
        >
          {gate.trend === 0 ? 'flat' : `${gate.trend > 0 ? '+' : ''}${gate.trend}pp`}
        </span>

        {/* Count */}
        <span className="text-xs text-secondary w-16 text-right flex-shrink-0">
          ({gate.passCount}/{gate.passCount + gate.failCount})
        </span>

        {/* Expand toggle */}
        <span className="text-xs text-secondary w-4">{isExpanded ? '\u25B2' : '\u25BC'}</span>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mx-4 mb-2 rounded-lg bg-surface shadow-sm p-3 border border-border-subtle">
          {/* Failing creatives */}
          {gate.failingCreatives.length > 0 && (
            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="text-xs font-semibold text-danger mb-2">Failing creatives:</div>
              <div className="flex flex-wrap gap-1.5">
                {gate.failingCreatives.slice(0, 5).map((c) => (
                  <button
                    key={c.creativeId}
                    className="text-xs px-2 py-0.5 rounded bg-danger/10 text-danger hover:bg-danger/20 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/creatives/${c.creativeId}`);
                    }}
                  >
                    {c.name}
                  </button>
                ))}
                {gate.failingCreatives.length > 5 && (
                  <button
                    className="text-xs text-brand hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrilldown(gate.gateId);
                    }}
                  >
                    +{gate.failingCreatives.length - 5} more
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Passing creatives */}
          {gate.passingCreatives && gate.passingCreatives.length > 0 && (
            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="text-xs font-semibold text-success mb-2">Passing creatives:</div>
              <div className="flex flex-wrap gap-1.5">
                {gate.passingCreatives.slice(0, 5).map((c) => (
                  <button
                    key={c.creativeId}
                    className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/creatives/${c.creativeId}`);
                    }}
                  >
                    {c.name}
                  </button>
                ))}
                {gate.passingCreatives.length > 5 && (
                  <button
                    className="text-xs text-brand hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrilldown(gate.gateId);
                    }}
                  >
                    +{gate.passingCreatives.length - 5} more
                  </button>
                )}
              </div>
            </div>
          )}

          {/* View all in Creatives tab */}
          <div className="mt-3 border-t border-border-subtle pt-2">
            <button
              className="text-xs text-brand hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onDrilldown(gate.gateId);
              }}
            >
              View all in Creatives tab
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function GateBars() {
  const { filters, setFilter, setTab } = useDashboardContext();
  const { data, isLoading, error } = useGateSummary(filters);
  const [expandedGate, setExpandedGate] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (isLoading) return <GatesSkeleton />;

  if (error) {
    return (
      <div className="rounded-xl bg-danger/5 border border-danger/20 px-4 py-3">
        <div className="text-sm font-semibold text-danger">Failed to load quality gates</div>
      </div>
    );
  }

  const gates = data?.gates;
  if (!gates || gates.length === 0) {
    return <EmptyState tab="gates" />;
  }

  // Backend returns pre-sorted: critical first, passRate ascending
  const visibleGates = showAll ? gates : gates.slice(0, 6);

  // Drilldown: navigate to Creatives tab with gate filter (FR-25)
  function handleDrilldown(gateId: string) {
    setFilter('drilldownGate', gateId);
    setTab('creatives');
  }

  // CSV export data
  const csvHeaders = ['Gate ID', 'Name', 'Role', 'Pass Rate', 'Pass Count', 'Fail Count'];
  const csvRows = gates.map((g) => [
    g.gateId,
    g.name,
    g.role,
    g.passRate,
    g.passCount,
    g.failCount,
  ]);

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="text-sm font-semibold text-primary">Gate Pass Rates</div>
        <CsvExport headers={csvHeaders} rows={csvRows} filename="gate-pass-rates" />
      </div>

      <div className="py-2 space-y-0.5">
        {visibleGates.map((gate, idx) => (
          <GateRow
            key={gate.gateId}
            gate={gate}
            isExpanded={expandedGate === gate.gateId}
            onToggle={() => setExpandedGate((prev) => (prev === gate.gateId ? null : gate.gateId))}
            isOdd={idx % 2 === 1}
            onDrilldown={handleDrilldown}
          />
        ))}
      </div>

      {gates.length > 6 && (
        <div className="px-4 py-2 border-t border-border-subtle">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-xs text-brand hover:underline"
          >
            {showAll ? 'Show fewer gates' : `Show all ${gates.length} gates`}
          </button>
        </div>
      )}
    </div>
  );
}
